import { useState } from 'react';
import type { CharacterClass } from '@/domain/types';
import './AuthScreen.css';
import './NewCharacterScreen.css';

interface ClassOption {
  id: CharacterClass;
  name: string;
  tagline: string;
  /** Marked false = locked / coming-later. Currently all classes other than
   *  HK are forward-compat placeholders that don't actually exist as a
   *  body GLB. Phase P3 adds Phantom + Engineer; we'll flip those to
   *  available then. */
  available: boolean;
}

const CLASSES: ClassOption[] = [
  { id: 'hollow_knight', name: 'Hollow-Knight', tagline: 'Bulwark · Soul-Sparks · Tank-Build', available: true },
];

interface Props {
  /** Which slot (0..2) this new character will occupy. Display-only — the
   *  caller already chose the slot before opening this screen. */
  slotIndex: number;
  onCreate: (name: string, characterClass: CharacterClass) => Promise<void>;
  onCancel: () => void;
}

export function NewCharacterScreen({ slotIndex, onCreate, onCancel }: Props) {
  const [name, setName] = useState('');
  const [selectedClass, setSelectedClass] = useState<CharacterClass>('hollow_knight');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) { setError('Mindestens 2 Zeichen.'); return; }
    if (trimmed.length > 20) { setError('Maximal 20 Zeichen.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onCreate(trimmed, selectedClass);
    } catch (err) {
      setError((err as Error).message ?? 'Erstellung fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-screen__bg" aria-hidden />
      <div className="auth-screen__content">
        <h1 className="auth-screen__title">NEUER CHARAKTER</h1>
        <p className="auth-screen__tagline">Slot {slotIndex + 1} / 3</p>

        <form className="auth-screen__form" onSubmit={handleSubmit}>
          <label className="auth-screen__label">
            <span>Charakter-Name</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Jasmin"
              maxLength={20}
              required
              minLength={2}
            />
          </label>

          <div className="new-char__class-picker">
            <span className="new-char__class-label">Klasse</span>
            <div className="new-char__class-grid">
              {CLASSES.map((cls) => (
                <button
                  key={cls.id}
                  type="button"
                  className={
                    'new-char__class-card' +
                    (selectedClass === cls.id ? ' new-char__class-card--selected' : '') +
                    (cls.available ? '' : ' new-char__class-card--locked')
                  }
                  onClick={() => cls.available && setSelectedClass(cls.id)}
                  disabled={!cls.available}
                >
                  <div className="new-char__class-name">{cls.name}</div>
                  <div className="new-char__class-tagline">{cls.tagline}</div>
                  {!cls.available && <div className="new-char__class-locked-tag">Bald</div>}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="auth-screen__error">⛔ {error}</div>}

          <div className="new-char__buttons">
            <button
              type="button"
              className="btn"
              onClick={onCancel}
              disabled={busy}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={busy || name.trim().length < 2}
            >
              {busy ? 'Erstelle …' : 'Charakter erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
